'use client';

/**
 * ClaimModal — the sign-in-to-claim bridge.
 *
 * States, in order:
 *   review  → summary of what's about to be imported + phone/OTP if not signed in
 *   sending → sending OTP
 *   otp     → user entered OTP, awaiting verify
 *   claiming→ POST /api/advocate-search/claim
 *   done    → redirect to /dashboard?welcome=1
 *   error   → recoverable failure; form stays open
 *
 * Already-signed-in users skip straight from `review` to `claiming`. OTP
 * flow reuses the site-wide useAuth() context so the Supabase session is
 * established (cookies set) before the claim request is sent.
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth/context';
import CountryCodeSelector from '@/components/CountryCodeSelector';
import {
  DEFAULT_COUNTRY,
  formatPhoneE164,
  getMaxPhoneLength,
  validatePhone,
  type Country,
} from '@/lib/phone';
import { TermsMarketingConsentLabel } from '@/components/auth/TermsMarketingConsentLabel';
import { TERMS_ACCEPTANCE_REQUIRED_MESSAGE } from '@/lib/marketing/consent-copy';

type Phase = 'review' | 'sending' | 'otp' | 'verifying' | 'claiming' | 'done' | 'error';

interface ClaimModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  selectedCaseKeys: string[];
  selectedGroupKeys: string[];
  queryName: string;
  onClaimed: (imported: number) => void;
}

export default function ClaimModal({
  open,
  onClose,
  sessionId,
  selectedCaseKeys,
  selectedGroupKeys,
  queryName,
  onClaimed,
}: ClaimModalProps) {
  const { isAuthenticated, sendWhatsAppOtp, verifyWhatsAppOtp } = useAuth();

  const [phase, setPhase] = useState<Phase>('review');
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [imported, setImported] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const termsAcceptedRef = useRef(true);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Cooldown tick
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Reset state each open
  useEffect(() => {
    if (!open) return;
    setPhase('review');
    setOtp('');
    setErrorMsg(null);
    termsAcceptedRef.current = true;
    setTermsAccepted(true);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'claiming' && phase !== 'verifying' && phase !== 'sending') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, phase, onClose]);

  if (!open) return null;

  async function postClaim() {
    setPhase('claiming');
    setErrorMsg(null);
    try {
      const res = await fetch('/api/advocate-search/claim', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sessionId, selectedCaseKeys, selectedGroupKeys }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403 && data?.code === 'SUBSCRIPTION_LIMIT') {
          setErrorMsg(data?.reason || 'You need an active subscription to import cases.');
          setPhase('error');
          return;
        }
        if (res.status === 410) {
          setErrorMsg('This search expired. Please search again.');
          setPhase('error');
          return;
        }
        setErrorMsg(data?.reason || data?.error || 'Could not import cases. Please try again.');
        setPhase('error');
        return;
      }
      setImported(Number(data.imported) || 0);
      setPhase('done');
      onClaimed(Number(data.imported) || 0);
      // Give the user a beat to see the success message, then redirect.
      setTimeout(() => {
        window.location.assign(data.redirectTo || '/dashboard?welcome=1');
      }, 1200);
    } catch (err) {
      console.error('[ClaimModal] claim failed:', err);
      setErrorMsg('Network error. Please try again.');
      setPhase('error');
    }
  }

  async function handleSendOtp() {
    setErrorMsg(null);
    const currentTermsAccepted = termsAcceptedRef.current;
    if (!currentTermsAccepted) {
      setErrorMsg(TERMS_ACCEPTANCE_REQUIRED_MESSAGE);
      return;
    }
    const validation = validatePhone(phone, country.code);
    if (!validation.isValid) {
      setErrorMsg(validation.error || 'Enter a valid phone number.');
      return;
    }
    const formatted = formatPhoneE164(phone, country.dialCode);
    if (!formatted) {
      setErrorMsg('Enter a valid phone number.');
      return;
    }
    setPhase('sending');
    const { error } = await sendWhatsAppOtp(formatted, currentTermsAccepted);
    if (error) {
      setErrorMsg(error.message || 'Could not send the code. Please try again.');
      setPhase('review');
      return;
    }
    setPhase('otp');
    setResendCooldown(45);
  }

  async function handleVerifyOtp() {
    setErrorMsg(null);
    const currentTermsAccepted = termsAcceptedRef.current;
    if (!currentTermsAccepted) {
      setErrorMsg(TERMS_ACCEPTANCE_REQUIRED_MESSAGE);
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setErrorMsg('Enter the 6-digit code.');
      return;
    }
    const formatted = formatPhoneE164(phone, country.dialCode);
    if (!formatted) {
      setErrorMsg('Enter a valid phone number.');
      return;
    }
    setPhase('verifying');
    const { error } = await verifyWhatsAppOtp(formatted, otp, undefined, undefined, currentTermsAccepted);
    if (error) {
      setErrorMsg(error.message || 'Invalid code. Please try again.');
      setPhase('otp');
      return;
    }
    // Supabase session is now set — claim can proceed.
    await postClaim();
  }

  // Authenticated users skip the phone flow entirely — clicking the CTA on
  // review runs the claim straight away.
  async function handlePrimary() {
    if (isAuthenticated) {
      await postClaim();
      return;
    }
    if (phase === 'review' || phase === 'error') {
      await handleSendOtp();
    } else if (phase === 'otp') {
      await handleVerifyOtp();
    }
  }

  const busy = phase === 'sending' || phase === 'verifying' || phase === 'claiming';
  const maxLen = getMaxPhoneLength(country);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="claim-modal-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-6"
      onClick={e => {
        if (busy) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
      >
        <div className="flex items-start justify-between border-b border-line-soft p-5 sm:p-6">
          <div>
            <p className="eyebrow mb-1">Claim</p>
            <h2 id="claim-modal-title" className="font-serif text-xl text-ink sm:text-2xl">
              {phase === 'done'
                ? 'Imported.'
                : `Import ${selectedCaseKeys.length} case${selectedCaseKeys.length === 1 ? '' : 's'}`}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="rounded-full p-1.5 text-ink-4 transition hover:bg-paper-sunken disabled:opacity-40"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        <div className="p-5 sm:p-6">
          {phase !== 'done' && (
            <p className="text-sm text-ink-3">
              Cases will land in your dashboard as <span className="italic">{queryName}</span>.
              We&apos;ll keep details fresh as hearings are scheduled and orders posted.
            </p>
          )}

          {errorMsg && phase !== 'done' && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {errorMsg}
              {errorMsg.toLowerCase().includes('subscription') && (
                <> <Link className="font-medium underline" href="/pricing">View plans →</Link></>
              )}
            </div>
          )}

          {!isAuthenticated && (phase === 'review' || phase === 'sending') && (
            <div className="mt-5 space-y-2">
              <label className="block text-sm font-medium text-ink-2" htmlFor="claim-phone">
                WhatsApp number
              </label>
              <div className="flex items-stretch gap-2">
                <CountryCodeSelector selectedCountry={country} onChange={setCountry} disabled={busy} />
                <input
                  id="claim-phone"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, maxLen))}
                  placeholder={`e.g. ${country.example ?? '9999999999'}`}
                  autoComplete="tel"
                  inputMode="numeric"
                  maxLength={maxLen}
                  disabled={busy}
                  className="flex-1 rounded-lg border border-line bg-white px-4 py-2.5 text-ink placeholder:text-ink-5
                             focus:outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-200
                             disabled:bg-paper-sunken disabled:text-ink-4"
                />
              </div>
              <p className="text-xs text-ink-4">
                We&apos;ll send a 6-digit code. Standard WhatsApp rates apply.
              </p>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-line bg-paper-sunken p-3 text-xs leading-5 text-ink-3">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => {
                    termsAcceptedRef.current = event.target.checked;
                    setTermsAccepted(event.target.checked);
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-line text-primary focus:ring-primary"
                  required
                />
                <TermsMarketingConsentLabel />
              </label>
              {!termsAccepted && (
                <p role="alert" aria-live="polite" className="text-xs font-medium text-red-700">
                  {TERMS_ACCEPTANCE_REQUIRED_MESSAGE}
                </p>
              )}
            </div>
          )}

          {!isAuthenticated && (phase === 'otp' || phase === 'verifying') && (
            <div className="mt-5 space-y-2">
              <label className="block text-sm font-medium text-ink-2" htmlFor="claim-otp">
                6-digit code
              </label>
              <input
                id="claim-otp"
                type="text"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                disabled={busy}
                className="w-full rounded-lg border border-line bg-white px-4 py-2.5 text-center mono
                           text-xl tracking-[0.5em] focus:outline-none focus:border-primary-700
                           focus:ring-2 focus:ring-primary-200 disabled:bg-paper-sunken"
              />
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  disabled={resendCooldown > 0 || busy}
                  onClick={handleSendOtp}
                  className="text-ink-4 underline-offset-4 hover:underline disabled:text-ink-5 disabled:no-underline"
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : 'Resend code'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => { setPhase('review'); setOtp(''); setErrorMsg(null); }}
                  className="text-ink-4 underline-offset-4 hover:underline"
                >
                  Change number
                </button>
              </div>
            </div>
          )}

          {phase === 'claiming' && (
            <div className="mt-5 flex items-center gap-3 text-sm text-ink-3">
              <Spinner />
              Importing your cases…
            </div>
          )}

          {phase === 'done' && (
            <div className="mt-1 space-y-2 text-sm text-ink-2">
              <p>
                We imported <span className="font-medium">{imported}</span> case
                {imported === 1 ? '' : 's'} into your dashboard.
              </p>
              <p className="text-ink-4">
                Full hearing history, orders, and party details fill in over the next minute —
                your dashboard will update as it arrives.
              </p>
            </div>
          )}
        </div>

        {phase !== 'done' && (
          <div className="flex gap-3 border-t border-line-soft p-5 sm:p-6">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="flex-1 rounded-lg border border-line bg-white px-4 py-2.5 text-sm font-medium text-ink-2
                         hover:bg-paper-sunken disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePrimary}
              disabled={busy || (!isAuthenticated && (phase === 'review' || phase === 'error') && !termsAccepted)}
              className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white
                         shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2
                         focus:ring-primary-300 focus:ring-offset-2 disabled:bg-ink-5
                         disabled:cursor-not-allowed"
            >
              {primaryLabel(phase, isAuthenticated, selectedCaseKeys.length)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function primaryLabel(phase: Phase, isAuth: boolean, count: number): string {
  if (phase === 'sending') return 'Sending…';
  if (phase === 'verifying') return 'Verifying…';
  if (phase === 'claiming') return 'Importing…';
  if (phase === 'otp') return 'Verify + import';
  if (isAuth) return `Import ${count} case${count === 1 ? '' : 's'}`;
  return 'Send code';
}

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin text-primary-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
    </svg>
  );
}
